import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  CardFooter,
  CardHeader,
  Chip,
  ChipGroup,
  DegradedState,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  MessageBody,
  Meta,
  Section,
} from '../ds'
import {
  governanceApi,
  workspacesApi,
  type EscalationItem,
  type QuestionItem,
  type ShadowDecision,
} from '../services/api'
import { answerOutcome, closeOutcome } from '../components/chat/answerOutcome'
import { shadowReasoning, shadowSummary } from '../lib/activityNarrative'
import type { NeedsYou } from './useNeedsYou'

// One inbox over both notification kinds. Questions and escalations are two
// endpoints over one shared `notifications` table, split only by a `type`
// filter server-side; this puts them back together, which is how the merchant
// experiences them — a single list of things waiting on their call.
//
// It replaces /notifications as a *surface*. The route stays as a redirect so
// existing links and the old bell destination keep working.

type Filter = 'all' | 'question' | 'escalation'

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'question', label: 'Questions' },
  { id: 'escalation', label: 'Escalations' },
]

/** Fallback only — the server's own copy is shown when it sends one. */
const ERASED_COPY = 'This customer asked to be forgotten — nothing was sent.'

const when = (iso?: string) => (iso ? iso.slice(0, 16).replace('T', ' ') : '')

/** A card's description lines, one per line; absent ones drop out (undefined when none). */
function lines(...parts: Array<string | false | null | undefined>) {
  const shown = parts.filter((p): p is string => !!p)
  if (!shown.length) return undefined
  return shown.map((p, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {p}
    </Fragment>
  ))
}

interface Props {
  open: boolean
  onClose: () => void
  /** Fires when the slide-out has finished (the Drawer's `onExited`): the shell keeps the
   *  drawer registered until then, so the dock's count follows what is on screen. */
  onExited?: () => void
  workspaceId: string | undefined
  needsYou: NeedsYou
}

/**
 * The Inbox's answer copy, from the return the server attempted (MAR-175) — the same words as the
 * Ask receipt, never a bare "Sent": only an executed return reached the customer. The answer is
 * kept for reuse unless the customer was erased (erasure deletes the remembered copy too).
 */
function sentence(outcome: string): string {
  return `${outcome.charAt(0).toUpperCase()}${outcome.slice(1)}.`
}

function answerMessage(returnStatus: string | undefined): string {
  const said = sentence(answerOutcome(returnStatus))
  return returnStatus === 'customer_erased' ? said : `${said} She keeps it as an answer she can reuse.`
}

/** The Inbox's decline copy — the honest close is a return too, so only `executed` says she closed it. */
function declineMessage(returnStatus: string | undefined): string {
  return sentence(closeOutcome(returnStatus))
}

export default function InboxDrawer({ open, onClose, onExited, workspaceId, needsYou }: Props) {
  const {
    escalations: allEscalations,
    questions: allQuestions,
    shadow,
    shadowHasMore,
    refresh,
    degraded,
    unknown,
    initialising,
    pending,
  } = needsYou
  const shadowPending = pending.includes('shadow')
  const [filter, setFilter] = useState<Filter>('all')
  const [editing, setEditing] = useState<string | null>(null)
  // One draft per item. A single shared draft silently discarded what you had
  // typed the moment you opened another question's composer.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [conflict, setConflict] = useState<Record<string, string>>({})
  // Items resolved in this session, hidden immediately rather than waiting for
  // the refetch — otherwise a just-approved item stays clickable for a beat and
  // a second click reports a conflict that the merchant caused by clicking twice.
  const [settled, setSettled] = useState<string[]>([])
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setEditing(null)
    setDrafts({})
    setMessage(null)
    setConflict({})
    setSettled([])
  }, [open])

  // Focus, the Tab trap, Escape and restoring focus to the opener are the
  // design-system Drawer's — one Escape stack for every overlay (overlays.md).
  // The Inbox used to carry its own copy of each; it was the reference the
  // Drawer was built from (migration.md).
  // ⚠️ Always mounted (MAR-381): the Drawer flips `open`, slides, and holds what the Inbox last
  // showed through the slide-out. Closed, everything below is built and then ignored by it.

  const escalations = allEscalations.filter((e) => !settled.includes(e.notificationId))
  const questions = allQuestions.filter((q) => !settled.includes(q.notificationId))

  const counts: Record<Filter, number> = {
    all: escalations.length + questions.length + shadow.length,
    question: questions.length,
    escalation: escalations.length,
  }

  // A 409 means it was already settled elsewhere — most often the merchant
  // answered Marva's email. That is the system working, not an error, so it
  // gets its own card rather than a red banner.
  // governance-api says "Question is no longer pending (already resolved or
  // expired)" and the escalation equivalent. Match that phrase exactly and
  // nothing looser: its 500 handler forwards `error.message` verbatim, so a
  // real failure ("The security token ... is expired", anything containing
  // "already"/"resolved") would otherwise be shown to the merchant as a
  // successful conflict — a broken action reported as a settled one.
  const settledElsewhere = (e: unknown) => e instanceof Error && /no longer pending/i.test(e.message)

  // The customer asked to be forgotten (MAR-175): erasure moved the question to its terminal
  // `erased` status, and the answer/decline route refuses with 409 `question_erased`. That is
  // settled, not failed — nothing was sent, and there is nothing left to act on — so the item
  // leaves the list and the SERVER's copy is shown. Keyed on `details.reason`, never the prose.
  const questionErased = (res: { status?: number; details?: unknown }) =>
    res.status === 409 &&
    !!res.details &&
    typeof res.details === 'object' &&
    !Array.isArray(res.details) &&
    (res.details as { reason?: unknown }).reason === 'question_erased'

  const resolveEscalation = async (item: EscalationItem, decision: 'approve' | 'reject') => {
    if (!workspaceId) return
    setBusy(item.notificationId)
    setMessage(null)
    try {
      const res = await governanceApi.resolveEscalation(workspaceId, item.notificationId, decision, {
        createdAt: item.createdAt,
      })
      if (!res.success) throw new Error(res.error || 'Could not resolve — it may have expired')
      setSettled((s) => [...s, item.notificationId])
      setMessage(
        decision === 'approve' ? 'Approved. It goes out inside your limits.' : 'Rejected. She records why and adjusts.'
      )
      refresh()
    } catch (e) {
      if (settledElsewhere(e)) {
        setConflict((c) => ({ ...c, [item.notificationId]: 'This was already settled — nothing was sent twice.' }))
      } else {
        setMessage(e instanceof Error ? e.message : 'Could not resolve escalation')
      }
      refresh()
    } finally {
      setBusy(null)
    }
  }

  const resolveQuestion = async (item: QuestionItem, action: 'answer' | 'decline') => {
    if (!workspaceId) return
    const answerText = (drafts[item.notificationId] || '').trim()
    if (action === 'answer' && !answerText) {
      setMessage('Type the answer first.')
      return
    }
    setBusy(item.notificationId)
    setMessage(null)
    try {
      const res =
        action === 'answer'
          ? await governanceApi.answerQuestion(workspaceId, item.notificationId, {
              createdAt: item.createdAt,
              answerText,
            })
          : await governanceApi.declineQuestion(workspaceId, item.notificationId, { createdAt: item.createdAt })
      if (!res.success && questionErased(res)) {
        setSettled((s) => [...s, item.notificationId])
        setMessage(res.error || ERASED_COPY)
        setEditing(null)
        setDrafts((d) => {
          const next = { ...d }
          delete next[item.notificationId]
          return next
        })
        refresh()
        return
      }
      if (!res.success) throw new Error(res.error || 'Could not resolve — it may have expired')
      setSettled((s) => [...s, item.notificationId])
      setMessage(action === 'answer' ? answerMessage(res.data?.returnStatus) : declineMessage(res.data?.returnStatus))
      setEditing(null)
      setDrafts((d) => {
        const next = { ...d }
        delete next[item.notificationId]
        return next
      })
      refresh()
    } catch (e) {
      if (settledElsewhere(e)) {
        setConflict((c) => ({
          ...c,
          [item.notificationId]: 'You already answered this by email, so she closed it — nothing was sent twice.',
        }))
        setEditing(null)
      } else {
        setMessage(e instanceof Error ? e.message : 'Could not resolve question')
      }
      refresh()
    } finally {
      setBusy(null)
    }
  }

  // Shadow drafts take feedback, not release. `submitShadowFeedback` records
  // {approved} as a training signal — there is no path in the backend that
  // sends a shadow-held draft, so this must not say "release" or "send".
  const judgeShadow = async (item: ShadowDecision, approved: boolean) => {
    if (!workspaceId) return
    setBusy(item.decisionId)
    setMessage(null)
    try {
      const res = await workspacesApi.submitShadowFeedback(workspaceId, item.decisionId, { approved })
      if (!res.success) throw new Error(res.error || 'Could not record that')
      setMessage(
        approved
          ? 'Noted as a good call. She learns from it — nothing was sent.'
          : 'Noted as the wrong call. She records why and adjusts.'
      )
      refresh()
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not record that')
    } finally {
      setBusy(null)
    }
  }

  const showQuestions = filter === 'all' || filter === 'question'
  const showEscalations = filter === 'all' || filter === 'escalation'
  const showShadow = filter === 'all'
  const visibleNotifications = (showQuestions ? questions.length : 0) + (showEscalations ? escalations.length : 0)
  // A pending Shadow section counts as something on screen, so the all-clear
  // does not render alongside "checking what she is holding".
  const visible = visibleNotifications + (showShadow ? shadow.length : 0) + (showShadow && shadowPending ? 1 : 0)

  /** The time and the surface an item belongs to, under its kind. */
  const kindRow = (createdAt: string | undefined, surface: string | undefined) => (
    <>
      <Meta>{when(createdAt)}</Meta>
      {surface && (
        <>
          {' · '}
          {surface}
        </>
      )}
    </>
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onExited={onExited}
      scope={workspaceId}
      size="lg"
      // v6's head (prototype L1393): the title IS "Waiting on you", with no eyebrow over it.
      // It was "One inbox" under a "Waiting on you" eyebrow — the same words, demoted (MAR-317).
      title="Waiting on you"
      subtitle={
        <>
          Questions she cannot answer, and permission she does not have. Reply to her email and it is done.{' '}
          <Link to="/account?tab=notifications" onClick={onClose}>
            Change how you hear about these →
          </Link>
        </>
      }
    >
      <ChipGroup label="Show">
        {FILTERS.map((f) => (
          <Chip
            key={f.id}
            kind="select"
            selected={filter === f.id}
            onToggle={() => setFilter(f.id)}
            // A full held-drafts page is a floor, as the bell and Ledger's banner say.
            count={`${counts[f.id]}${f.id === 'all' && shadowHasMore ? '+' : ''}`}
          >
            {f.label}
          </Chip>
        ))}
      </ChipGroup>

      {degraded && visible > 0 && (
        <DegradedState
          title={
            unknown
              ? 'Part of the queue could not be read, so there may be more than this.'
              : 'Showing the last successful check — the queue could not be refreshed just now.'
          }
          retryLabel="Try again"
          onRetry={refresh}
        />
      )}

      {visibleNotifications > 0 && (
        <>
          {showQuestions &&
            questions.map((item) => (
              <Card key={item.notificationId} tone="gold" as="article">
                <CardHeader
                  titleAs="p"
                  icon={
                    <Chip
                      tone="gold"
                      title="notificationType: customer_question — a knowledge gap, answered back to a waiting customer"
                    >
                      She needs an answer
                    </Chip>
                  }
                  description={kindRow(item.createdAt, 'Knowledge')}
                />
                <CardHeader
                  titleAs="p"
                  title={`“${item.question}”`}
                  description={lines(
                    item.customerRef,
                    item.expiresAt && `She’ll close it out honestly if unanswered by ${when(item.expiresAt)}`,
                  )}
                />
                {item.suggestedAnswer && (
                  <Card tone="recessed" padding="sm" as="blockquote">
                    Her guess, held back: {item.suggestedAnswer}
                  </Card>
                )}

                {conflict[item.notificationId] ? (
                  <Alert tone="info">{conflict[item.notificationId]}</Alert>
                ) : editing === item.notificationId ? (
                  <>
                    <Field
                      as="textarea"
                      rows={3}
                      aria-label={`Your answer to: ${item.question}`}
                      value={drafts[item.notificationId] ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setDrafts((d) => ({ ...d, [item.notificationId]: e.target.value }))
                      }
                      placeholder="Answer in your own words…"
                      hint="She delivers this in the channel they were last in, and keeps it as an answer she can reuse."
                    />
                    <CardFooter>
                      <Button
                        size="sm"
                        variant="gold"
                        disabled={busy === item.notificationId}
                        onClick={() => resolveQuestion(item, 'answer')}
                      >
                        Send it and remember it
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </CardFooter>
                  </>
                ) : (
                  <CardFooter>
                    <Button
                      size="sm"
                      variant="gold"
                      onClick={() =>
                        setEditing((cur) => {
                          // Seed with her guess only the first time; a draft
                          // already typed for this item survives reopening.
                          setDrafts((d) =>
                            item.notificationId in d ? d : { ...d, [item.notificationId]: item.suggestedAnswer || '' }
                          )
                          return cur === item.notificationId ? cur : item.notificationId
                        })
                      }
                    >
                      Answer her
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === item.notificationId}
                      onClick={() => resolveQuestion(item, 'decline')}
                    >
                      Decline
                    </Button>
                  </CardFooter>
                )}
              </Card>
            ))}

          {showEscalations &&
            escalations.map((item) => (
              <Card key={item.notificationId} as="article">
                <CardHeader
                  titleAs="p"
                  icon={
                    <Chip title="notificationType: escalation — a threshold breach, you grant or refuse">
                      She needs permission
                    </Chip>
                  }
                  description={kindRow(item.createdAt, 'Authority')}
                />
                <CardHeader
                  titleAs="p"
                  title={item.title}
                  description={lines(item.summary, item.customerRef, item.expiresAt && `Expires ${when(item.expiresAt)}`)}
                />

                {conflict[item.notificationId] ? (
                  <Alert tone="info">{conflict[item.notificationId]}</Alert>
                ) : (
                  <CardFooter>
                    <Button
                      size="sm"
                      variant="gold"
                      disabled={busy === item.notificationId}
                      onClick={() => resolveEscalation(item, 'approve')}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === item.notificationId}
                      onClick={() => resolveEscalation(item, 'reject')}
                    >
                      Reject
                    </Button>
                  </CardFooter>
                )}
              </Card>
            ))}
        </>
      )}

      {showShadow && (shadow.length > 0 || shadowPending) && (
        // The heading and its cards are siblings in the drawer body, so the body's
        // own gap spaces the cards (a Section's children are not spaced).
        <>
          <Section
            title="Held by Shadow"
            description={
              shadowPending
                ? // Just entered Shadow: the drafts have not been read yet.
                  // An absent section would read as "she is holding nothing".
                  'Checking what she is holding…'
                : 'She drafted these and sent nothing. Tell her which were the right call — it trains her judgement. Going live is a change to the rung, on Authority.'
            }
          />
          {shadow.map((item) => (
            <Card key={item.decisionId} tone="gold" as="article">
              <CardHeader
                titleAs="p"
                icon={<Chip>{item.trigger || item.type || 'Decision'}</Chip>}
                description={kindRow(item.createdAt, item.channel)}
              />
              <p className="t-card-title">{shadowReasoning(item) || shadowSummary(item)}</p>
              {item.message && (
                <Card tone="recessed" padding="sm" as="blockquote">
                  <MessageBody>{item.message}</MessageBody>
                </Card>
              )}
              <CardFooter>
                <Button
                  size="sm"
                  variant="gold"
                  disabled={busy === item.decisionId}
                  onClick={() => judgeShadow(item, true)}
                >
                  Right call
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === item.decisionId}
                  onClick={() => judgeShadow(item, false)}
                >
                  Wrong call
                </Button>
              </CardFooter>
            </Card>
          ))}
        </>
      )}

      {visible === 0 &&
        (initialising ? (
          // Nothing has been read yet, so there is nothing to be clear about.
          // A hanging request must not leave an all-clear up.
          <LoadingState caption="Checking what is waiting on you…" />
        ) : degraded ? (
          // Never claim the queue is clear on the strength of a read that
          // failed — that is how a merchant misses a governance decision. Say
          // what happened instead (D-26: a failed read is an ErrorState).
          <ErrorState title="Could not read the queue just now, so this may not be everything." onRetry={refresh}>
            It retries every minute.
          </ErrorState>
        ) : (
          <EmptyState size="sm">
            {counts.all === 0
              ? 'Nothing waiting. She handled the night inside your limits.'
              : 'Nothing of this kind waiting.'}
          </EmptyState>
        ))}

      {message && <Alert tone="info">{message}</Alert>}
    </Drawer>
  )
}
